import React, { useState } from 'react';
import { 
  Shield, Lock, Eye, Database, UserCheck, Mail, AlertTriangle, FileText, 
  ChevronDown, ChevronUp, Globe, Clock, Server, Key, Users, Scale
} from 'lucide-react';

const PrivacyPolicy = () => {
  const lastUpdated = "March 27, 2026";
  const [expandedSection, setExpandedSection] = useState(null);

  const sections = [
    {
      icon: Database, color: 'cyan',
      title: "Information We Collect",
      summary: "We collect information you provide directly and data generated through your use of our services.",
      content: [
        { subtitle: "Account Information", text: "When you create an account, we collect your username, email address, name, and a securely hashed password." },
        { subtitle: "Profile Information", text: "Optional details you choose to provide such as display name, phone number, date of birth, location, and bio." },
        { subtitle: "Communications", text: "Messages sent through our live chat and support systems, including chat transcripts with our AI assistant Nova." },
        { subtitle: "Usage Data", text: "Interaction logs, pages visited, features used, and session duration to improve our services." },
        { subtitle: "Device Information", text: "Browser type, operating system, and device identifiers for security and compatibility purposes." }
      ]
    },
    {
      icon: Eye, color: 'emerald',
      title: "How We Use Your Information",
      summary: "Your data helps us deliver, personalise, and improve our services.",
      content: [
        { subtitle: "Service Delivery", text: "To operate our platform, authenticate users, and provide customer support through live chat." },
        { subtitle: "Personalisation", text: "To tailor your experience, remember preferences, and auto-fill your details in support interactions." },
        { subtitle: "Communication", text: "To send account updates, newsletters (with your consent), and respond to support queries." },
        { subtitle: "Security", text: "To detect and prevent fraud, unauthorised access, and protect our users and infrastructure." },
        { subtitle: "Analytics", text: "To understand how our services are used and identify areas for improvement (anonymised where possible)." }
      ]
    },
    {
      icon: Lock, color: 'amber',
      title: "Data Security",
      summary: "We implement industry-standard security measures to protect your data.",
      content: [
        { subtitle: "Encryption", text: "All data in transit is encrypted using TLS/SSL. Sensitive data at rest is encrypted using AES-256 standards." },
        { subtitle: "Password Security", text: "Passwords are securely hashed using bcrypt and are never stored in plain text or visible to any staff member." },
        { subtitle: "Access Controls", text: "Access to personal data is restricted to authorised personnel on a need-to-know basis with role-based permissions." },
        { subtitle: "Monitoring", text: "We employ continuous monitoring and regular security audits to identify and address potential vulnerabilities." },
        { subtitle: "Incident Response", text: "In the unlikely event of a data breach, affected users will be notified within 72 hours as per GDPR requirements." }
      ]
    },
    {
      icon: UserCheck, color: 'purple',
      title: "Your Rights",
      summary: "You have comprehensive rights over your personal data under GDPR and UK data protection law.",
      content: [
        { subtitle: "Right of Access", text: "You can view all your personal data at any time through your account settings or by requesting a data export." },
        { subtitle: "Right to Rectification", text: "Update or correct your information directly in your profile settings at any time." },
        { subtitle: "Right to Erasure", text: "Delete your account and all associated data through the account settings. Deletion is processed within 30 days." },
        { subtitle: "Right to Data Portability", text: "Export your personal data in a machine-readable JSON format from your account settings." },
        { subtitle: "Right to Object", text: "Opt out of marketing communications and promotional emails at any time through your notification preferences." },
        { subtitle: "Right to Withdraw Consent", text: "Where processing is based on consent, you may withdraw it at any time without affecting prior processing." }
      ]
    },
    {
      icon: Clock, color: 'teal',
      title: "Data Retention",
      summary: "We retain your data only as long as necessary for the purposes it was collected.",
      content: [
        { subtitle: "Active Accounts", text: "Account data is maintained while your account is active and in use." },
        { subtitle: "Chat History", text: "Live chat transcripts are retained for up to 12 months for quality assurance and training purposes." },
        { subtitle: "Account Deletion", text: "Upon account deletion, personal data is permanently removed from our systems within 30 days." },
        { subtitle: "Anonymised Data", text: "Some anonymised, aggregated data may be retained indefinitely for analytical and statistical purposes." },
        { subtitle: "Legal Requirements", text: "Certain data may be retained longer where required by law or for legitimate business interests." }
      ]
    },
    {
      icon: Users, color: 'rose',
      title: "Data Sharing & Third Parties",
      summary: "We do not sell your personal data. Sharing is limited to essential service providers.",
      content: [
        { subtitle: "Service Providers", text: "We may share data with trusted partners who help us operate our services (hosting, analytics) under strict agreements." },
        { subtitle: "AI Services", text: "Chat interactions with our AI assistant Nova are processed by our AI service provider solely to generate responses." },
        { subtitle: "Legal Obligations", text: "We may disclose data when required by law, court order, or to protect the rights and safety of our users." },
        { subtitle: "No Selling of Data", text: "We never sell, rent, or trade your personal information to third parties for marketing purposes." }
      ]
    },
    {
      icon: Globe, color: 'sky',
      title: "Cookies & Tracking",
      summary: "We use essential cookies to maintain your session and preferences.",
      content: [
        { subtitle: "Essential Cookies", text: "Required for authentication, session management, and basic functionality. Cannot be disabled." },
        { subtitle: "Preference Cookies", text: "Store your settings like notification preferences and theme choices for a better experience." },
        { subtitle: "Analytics", text: "We use anonymised analytics to understand usage patterns. No personal data is shared with analytics providers." },
        { subtitle: "No Third-Party Ads", text: "We do not use advertising cookies or allow third-party ad networks to track you on our platform." }
      ]
    }
  ];

  const colorMap = {
    cyan: { bg: 'bg-cyan-500/15', border: 'border-cyan-500/20', text: 'text-cyan-400', dot: 'bg-cyan-400' },
    emerald: { bg: 'bg-emerald-500/15', border: 'border-emerald-500/20', text: 'text-emerald-400', dot: 'bg-emerald-400' },
    amber: { bg: 'bg-amber-500/15', border: 'border-amber-500/20', text: 'text-amber-400', dot: 'bg-amber-400' },
    purple: { bg: 'bg-purple-500/15', border: 'border-purple-500/20', text: 'text-purple-400', dot: 'bg-purple-400' },
    teal: { bg: 'bg-teal-500/15', border: 'border-teal-500/20', text: 'text-teal-400', dot: 'bg-teal-400' },
    rose: { bg: 'bg-rose-500/15', border: 'border-rose-500/20', text: 'text-rose-400', dot: 'bg-rose-400' },
    sky: { bg: 'bg-sky-500/15', border: 'border-sky-500/20', text: 'text-sky-400', dot: 'bg-sky-400' },
  };

  return (
    <div className="min-h-screen bg-[#030712] pt-24 pb-16" data-testid="privacy-policy-page">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-40 left-1/4 w-[600px] h-[600px] bg-cyan-500/3 rounded-full blur-[150px]" />
        <div className="absolute bottom-40 right-1/4 w-[500px] h-[500px] bg-indigo-500/3 rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-gradient-to-br from-cyan-500/20 to-indigo-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-cyan-500/20">
            <Scale className="w-8 h-8 text-cyan-400" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-cyan-400 via-sky-300 to-indigo-400 bg-clip-text text-transparent">
              Privacy Policy
            </span>
          </h1>
          <p className="text-slate-500 text-base max-w-xl mx-auto">
            How Starnight Holdings collects, uses, and protects your personal information
          </p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <Clock className="w-4 h-4 text-slate-600" />
            <span className="text-slate-600 text-sm">Last updated: {lastUpdated}</span>
          </div>
        </div>

        {/* Introduction */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 sm:p-8 mb-8">
          <p className="text-slate-300 leading-relaxed">
            At Starnight Holdings, we are committed to protecting your privacy and ensuring the security of your personal information. 
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our services, 
            in compliance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
          </p>
          <p className="text-slate-500 mt-3 text-sm">
            By using our services, you agree to the collection and use of information in accordance with this policy. 
            If you do not agree with any part of this policy, please do not use our services.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {sections.map((section, index) => {
            const isExpanded = expandedSection === index;
            const colors = colorMap[section.color];
            return (
              <div key={index} className={`bg-slate-900/50 border rounded-2xl overflow-hidden transition-all ${
                isExpanded ? `${colors.border} shadow-lg` : 'border-slate-800 hover:border-slate-700'
              }`}>
                <button onClick={() => setExpandedSection(isExpanded ? null : index)}
                  className="w-full p-5 sm:p-6 flex items-center gap-4 text-left">
                  <div className={`w-11 h-11 ${colors.bg} rounded-xl flex items-center justify-center flex-shrink-0 border ${colors.border}`}>
                    <section.icon className={`w-5 h-5 ${colors.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-white font-semibold text-base sm:text-lg">{section.title}</h2>
                    {!isExpanded && <p className="text-slate-500 text-sm mt-0.5 truncate">{section.summary}</p>}
                  </div>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all ${
                    isExpanded ? `${colors.bg}` : 'bg-slate-800'
                  }`}>
                    {isExpanded ? <ChevronUp className={`w-4 h-4 ${colors.text}`} /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                  </div>
                </button>
                {isExpanded && (
                  <div className="px-5 sm:px-6 pb-6 pt-0 space-y-3" style={{ animation: 'fadeInDown 0.2s ease-out' }}>
                    <div className="h-px bg-slate-800 mb-4" />
                    {section.content.map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className={`w-1.5 h-1.5 ${colors.dot} rounded-full mt-2 flex-shrink-0`} />
                        <div>
                          <span className="text-white text-sm font-medium">{item.subtitle}: </span>
                          <span className="text-slate-400 text-sm">{item.text}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Contact */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 sm:p-8 mt-8 text-center">
          <div className="w-12 h-12 bg-cyan-500/15 rounded-xl flex items-center justify-center mx-auto mb-4 border border-cyan-500/20">
            <Mail className="w-6 h-6 text-cyan-400" />
          </div>
          <h2 className="text-white font-semibold text-lg mb-2">Questions About Privacy?</h2>
          <p className="text-slate-400 text-sm mb-4 max-w-md mx-auto">
            If you have any questions or concerns about this Privacy Policy or wish to exercise your data rights, please contact our Data Protection Officer.
          </p>
          <a href="mailto:privacy@starnightholdings.uk"
            className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors font-medium text-sm">
            <Mail className="w-4 h-4" />
            privacy@starnightholdings.uk
          </a>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-slate-600 text-xs flex items-center justify-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5" />
            We may update this Privacy Policy from time to time. Continued use of our services constitutes acceptance of any changes.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default PrivacyPolicy;
